import { useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { Col, Row } from "react-bootstrap";
import { useForm, FormProvider } from "react-hook-form";
import { toast } from "react-toastify";
import { useQueryClient } from "@tanstack/react-query";

import Dialog from "components/organisms/Dialog/Dialog";
import Button from "components/atoms/Button/Button";
import errorToast from "helpers/errorToast";
import { declineApprovalRequest } from "api/services/quotation";
import Textarea from "components/atoms/Textarea/Textarea";
import { userQueryKeys } from "api/queries/user/userQueryKeys";

interface DeclineApprovalDialogProps {
  close: () => void;
  isOpen: boolean;
  fetchQuoteRequestData: () => void;
  quotationId: string;
}

function DeclineApprovalDialog({ close, isOpen, fetchQuoteRequestData, quotationId }: DeclineApprovalDialogProps) {
  const intl = useIntl();
  const methods = useForm();
  const queryClient = useQueryClient();
  const { handleSubmit } = methods;
  const [isSubmitting, setIsSubmitting] = useState(false);

  const onSubmit = (data: { comment?: string }) => {
    setIsSubmitting(true);
    declineApprovalRequest(quotationId, {
      comment: data.comment,
    })
      .then(() => {
        toast("Approval request declined", { type: "success" });
        queryClient.invalidateQueries({ queryKey: userQueryKeys.userPendingTasks });
        fetchQuoteRequestData();
        close();
      })
      .catch((err) => errorToast(err))
      .finally(() => setIsSubmitting(false));
  };

  return (
    <Dialog
      title={intl.formatMessage({
        id: "shipment-quotes.components.quotes.cancelApprovalRequestDialog.declineApprovalRequest",
        defaultMessage: "Decline approval request",
      })}
      show={isOpen}
      onHide={close}
    >
      <FormProvider {...methods}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <p className="mb-1 font-weight-bold">
            <FormattedMessage
              id="shipment-quotes.components.declineApprovalRequestDialog.cancelPrompt"
              defaultMessage="Are you sure you want to decline this request?"
            />
          </p>
          <div className="mt-4">
            <Textarea
              style={{
                border: "1px solid black",
                borderRadius: "13px !important",
              }}
              name="comment"
              className="rounded-textarea"
              rows={2}
              placeholder={intl.formatMessage({
                id: "shipment-quotes.components.quotes.declineApprovalRequestDialog.commentPlaceholder",
                defaultMessage: "Comments for rejection",
              })}
            />
          </div>
          <Row className="mt-5">
            <Col lg={12} className="d-flex flex-row align-items-center">
              <Button
                size="lg"
                className="btn-grey"
                label={intl.formatMessage({
                  id: "general.cancel",
                  defaultMessage: "Cancel",
                })}
                onClick={close}
              />
              <Button
                size="lg"
                className="ml-auto"
                isLoading={isSubmitting}
                variant="primary"
                label={intl.formatMessage({
                  id: "shipment-quotes.components.cancelApprovalRequestDialog.declineConfirmation",
                  defaultMessage: "Yes, decline request",
                })}
                type="submit"
              />
            </Col>
          </Row>
        </form>
      </FormProvider>
    </Dialog>
  );
}

export default DeclineApprovalDialog;
